import type { FC, ReactNode } from 'react'
import type { Category } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { countLabel } from '@/lib/format'
import { CoverImage } from '@/components/blog/cover-image'

interface TopicCardProps {
    category: Category
    headingLevel?: 'h2' | 'h3'
    className?: string
}

export const TopicCard: FC<TopicCardProps> = ({ category, headingLevel = 'h3', className }): ReactNode => {
    const Heading = headingLevel

    return (
        <article className={cn('group relative isolate flex aspect-[4/5] flex-col justify-end overflow-hidden rounded-2xl bg-muted', className)}>
            <div className='absolute inset-0 -z-10'>
                <CoverImage
                    src={category.coverImage}
                    alt={null}
                    className='transition-transform duration-700 ease-out group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100'
                />
            </div>

            <div aria-hidden='true' className='absolute inset-0 -z-10 bg-linear-to-t from-foreground/90 via-foreground/35 to-foreground/0' />

            <span
                aria-hidden='true'
                className='absolute right-5 top-5 inline-flex size-10 items-center justify-center rounded-full bg-background/90 text-foreground opacity-0 backdrop-blur transition-all duration-300 group-hover:opacity-100 motion-reduce:transition-none'
            >
                <ArrowUpRight className='size-4' />
            </span>

            <div className='p-6 text-primary-foreground'>
                <p className='text-xs font-semibold uppercase tracking-[0.16em] text-primary-foreground/75'>
                    {countLabel(category.postCount, 'story', 'stories')}
                </p>

                <Heading className='mt-2 font-heading text-3xl font-medium tracking-tight'>
                    <Link
                        href={`/topics/${category.slug}`}
                        className='after:absolute after:inset-0 after:rounded-2xl focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-4 focus-visible:after:outline-ring'
                    >
                        {category.name}
                    </Link>
                </Heading>

                {category.description && (
                    <p className='mt-2 text-sm leading-relaxed text-primary-foreground/80 line-clamp-3'>
                        {category.description}
                    </p>
                )}
            </div>
        </article>
    )
}
